"use client";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { formatCurrency, formatNumber } from "@/lib/utils";
import { motion } from "framer-motion";
import { DollarSign } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export default function BudgetCalculator() {
  const [downPayment, setDownPayment] = useState(50000);
  const [monthlyPayment, setMonthlyPayment] = useState(2000);
  const [interestRate, setInterestRate] = useState(3.5);
  const [maxHomePrice, setMaxHomePrice] = useState(0);
  const [loanAmount, setLoanAmount] = useState(0);

  const loanTerm = 30; // Assuming a 30-year fixed mortgage

  useEffect(() => {
    const calculateBudget = () => {
      const monthlyRate = interestRate / 100 / 12;
      const numberOfPayments = loanTerm * 12;
      const presentValue =
        // biome-ignore lint/style/useExponentiationOperator: skip
        (monthlyPayment * (1 - Math.pow(1 + monthlyRate, -numberOfPayments))) /
        monthlyRate;
      const totalBudget = presentValue + downPayment;
      const calculatedLoanAmount = totalBudget - downPayment;

      setMaxHomePrice(Math.round(totalBudget));
      setLoanAmount(Math.round(calculatedLoanAmount));
    };

    calculateBudget();
  }, [downPayment, monthlyPayment, interestRate]);

  const chartData = [
    { name: "Down Payment", value: downPayment },
    { name: "Loan Amount", value: loanAmount },
  ];

  return (
    <Card className="w-full max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-center">
          Home Buying Budget Calculator
        </CardTitle>
        <CardDescription className="text-center">
          Determine your maximum home price based on your down payment and
          comfortable monthly payment
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="downPayment" className="text-sm font-semibold">
              Down Payment
            </Label>
            <div className="flex items-center space-x-4">
              <div className="relative flex-1">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                <Input
                  id="downPayment"
                  type="text"
                  value={formatNumber(downPayment)}
                  onChange={(e) =>
                    setDownPayment(Number(e.target.value.replace(/,/g, "")))
                  }
                  className="pl-10"
                />
              </div>
              <Slider
                id="downPaymentSlider"
                min={0}
                max={500000}
                step={1000}
                value={[downPayment]}
                onValueChange={(value) => setDownPayment(value[0])}
                className="w-1/2"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="monthlyPayment" className="text-sm font-semibold">
              Comfortable Monthly Payment
            </Label>
            <div className="flex items-center space-x-4">
              <div className="relative flex-1">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                <Input
                  id="monthlyPayment"
                  type="text"
                  value={formatNumber(monthlyPayment)}
                  onChange={(e) =>
                    setMonthlyPayment(Number(e.target.value.replace(/,/g, "")))
                  }
                  className="pl-10"
                />
              </div>
              <Slider
                id="monthlyPaymentSlider"
                min={500}
                max={10000}
                step={100}
                value={[monthlyPayment]}
                onValueChange={(value) => setMonthlyPayment(value[0])}
                className="w-1/2"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="interestRate" className="text-sm font-semibold">
              Interest Rate: {interestRate.toFixed(2)}%
            </Label>
            <Slider
              id="interestRate"
              min={0.5}
              max={10}
              step={0.1}
              value={[interestRate]}
              onValueChange={(value) => setInterestRate(value[0])}
              className="mt-2"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Maximum Home Price</h3>
            <motion.div
              className="text-4xl font-bold text-primary"
              key={maxHomePrice}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              {formatCurrency(maxHomePrice)}
            </motion.div>
            <div className="space-y-2">
              <p className="text-sm">
                Down Payment:{" "}
                <span className="font-semibold">
                  {formatCurrency(downPayment)}
                </span>
              </p>
              <p className="text-sm">
                Loan Amount:{" "}
                <span className="font-semibold">
                  {formatCurrency(loanAmount)}
                </span>
              </p>
              <p className="text-sm">
                Monthly Payment:{" "}
                <span className="font-semibold">
                  {formatCurrency(monthlyPayment)}
                </span>
              </p>
              <p className="text-sm">
                Interest Rate:{" "}
                <span className="font-semibold">
                  {interestRate.toFixed(2)}%
                </span>
              </p>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              * This calculation assumes a 30-year fixed-rate mortgage.
            </p>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip
                  formatter={(value) => formatCurrency(value as number)}
                />
                <Legend verticalAlign="top" height={36} />
                <Bar dataKey="value" fill="#8884d8" name="Amount" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
